/**
 * Create different types of link based on Gatsby `Link` component
 *
 * Copyright (c) 2018-present, Luojun Jin
 *
 * @emails john.king.ca@gmail.com
 * @flow
 */

import {Link} from 'gatsby';
// import {Link} from '@reach/router';
import React from 'react';
import ExternalLinkSvg from 'templates/components/ExternalLinkSvg';
import slugify from 'utils/slugify';
import {colors, media} from 'theme';

// NOTE: https://flow.org/en/docs/react/types/#toc-react-node
import type {Node} from 'react';

type LinkBaseProps = {
  isActive: boolean,
  item: Object,
  section: Object,
};

/**
 * create a blog link, it will have a left bar before the active link
 *  - with brand color and
 *  - Active style
 *
 * @param {*} param0
 */
const createBlogLink = ({
  isActive,
  item,
  section,
}: LinkBaseProps): Node => {
  return (
    <Link css={[linkCss, isActive && activeLinkCss]} to={item.id}>
      {isActive && <span css={activeLinkBefore} />}
      {item.title}
    </Link>
  );
};

/**
 * create links for commnity section, two kinds
 *  1. with `href` set, external link
 *  2. regular in-site doc link
 *
 * @param {*} param0
 */
const createLinkCommunity = ({
  isActive,
  item,
  section,
}: LinkBaseProps): Node => {
  if (item.href) {
    return (
      <a css={[linkCss]} href={item.href} target="_blank" rel="noopener">
        {item.title}
        <ExternalLinkSvg
          cssProps={{
            verticalAlign: -2,
            display: 'inline-block',
            marginLeft: 5,
            color: colors.subtle,
          }}
        />
      </a>
    );
  }
  return createLinkDocs({
    isActive,
    item,
    section,
  });
};

/**
 * similar to createBlogLink
 * TODO: what's the difference between createBlogLink and createLinkDoc?
 *
 * @param {*} param0
 */
const createLinkDocs = ({
  isActive,
  item,
  section,
}: LinkBaseProps): Node => {
  return (
    <Link
      css={[linkCss, isActive && activeLinkCss]}
      to={slugify(item.id, section.directory)}>
      {isActive && <span css={activeLinkBefore} />}
      {item.title}
    </Link>
  );
};

/**
 * similar to createBlogLink
 * TODO: what's the difference between createBlogLink and createLinkDoc?
 *
 * @param {*} param0
 */
const createLinkDesign = ({
  isActive,
  item,
  section,
}: LinkBaseProps): Node => {
  return (
    <Link
      css={[linkCss, isActive && activeLinkCss]}
      to={slugify(item.id, section.directory)}>
      {isActive && <span css={activeLinkBefore} />}
      {item.title}
    </Link>
  );
};

// NOTE: use operator `&` as an Intersection Types
//  refert to: https://flow.org/en/docs/types/intersections/
type CreateLinkTutorialProps = {
  onLinkClick: Function,
} & LinkBaseProps;

/**
 * create link for each section of of a tutorial, it will handle click event when any section of the tutorial is clicked.
 * @param {*} param0
 */
const createLinkTutorial = ({
  isActive,
  item,
  onLinkClick,
  section,
}: CreateLinkTutorialProps): Node => {
  return (
    <Link
      css={[linkCss, isActive && activeLinkCss]}
      onClick={onLinkClick}
      to={item.href}>
      {isActive && <span css={activeLinkBefore} />}
      {item.title}
    </Link>
  );
};

/**
 * create link for each section of of a scripts, it will handle click event when each link clicked.
 * @param {*} param0
 */
const createDharmaScriptLink = ({
  isActive,
  item,
  onLinkClick,
  section,
  location,
}): Node => {
  let href = encodeURI(item.href);
  if (item.href) {
    item = { ...item, href: href};
  }

  // console.group('createDharmaScriptLink');
  // console.log(item.href);
  // console.log(href);
  // console.log(`location: ${location}`);
  // console.log(location);

  // console.groupEnd();

  return (
    <Link
      css={[linkCss, isActive && activeLinkCss]}
      onClick={onLinkClick}
      to={item.href}>
      {isActive && <span css={activeLinkBefore} />}
      {item.title}
    </Link>
  );
};

const activeLinkCss = {
  fontWeight: 700,
};

const activeLinkBefore = {
  width: 4,
  height: 25,
  borderLeft: `4px solid ${colors.brand}`,
  paddingLeft: 16,
  position: 'absolute',
  left: 0,
  marginTop: -3,

  [media.greaterThan('largerSidebar')]: {
    left: 15,
  },
};

const linkCss = {
  color: colors.text,
  display: 'inline-block',
  borderBottom: '1px solid transparent',
  transition: 'border 0.2s ease',
  marginTop: 5,

  '&:hover': {
    // color: colors.subtle,
    color: colors.brand,
  },
};

export {
  createBlogLink,
  createLinkCommunity,
  createLinkDocs,
  createLinkDesign,
  createLinkTutorial,
  createDharmaScriptLink,
};
